import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { Stack, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { APP_ICONS } from "../../../assets";
import AppLoader from "../../../components/AppLoader";
import PaperContainer from "../../../components/Containers/PaperContainer";
import GlobalButton from "../../../components/CustomButton";
import ClientOwnerFilterDialog from "../../../components/Dialog/ClientOwnerFilterDialog";
import StatusBlockDialog from "../../../components/Dialogs/StatusBlockDialog";
import DynamicPaginatedTable from "../../../components/Tables/DynamicPaginatedTable";
import TextInput from "../../../components/TextInputs";
import useDebounce from "../../../hooks/useDebounce";
import { setSearchKeys } from "../../../lib/slices/searchKeysSlice";
import { updateAllAcountStatus } from "../../../services/commonApiFn";
import { getAllSupplier } from "../../../services/supplierApiFn";
import { handleOpenSnackBar } from "../../../utils";
import { CSVLink } from "react-csv";
import moment from "moment";
import { useTranslation } from "react-i18next";

const headerData = [
  "createdAt",
  "supplierDetails",
  // "totalOrders",
  // "totalProducts",
  // "totalPackage",
  "status",
  // "Actions",
];

const tableRows = [
  "created_date",
  "supplier_details",
  // "totalOrders",
  // "totalProducts",
  // "totalPackage",
  "status",
  // "actions",
];

function SuppliersList() {
  const openBlockStatusDialog = useRef(null);
  const openFilterDialog = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(["client", "common"]);
  const [isLoading, setIsLoading] = useState(false);
  const [allSuppliers, setAllSuppliers] = useState();
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  const debouncedSearchTerm = useDebounce(searchKeys.supplierByName, 1000);

  const fetchAllSuppliers = async (name) => {
    try {
      setIsLoading(true);
      const response = await getAllSupplier(name);
      if (response.data.status === "success") {
        const data = response.data.data.suppliers;
        setAllSuppliers(data);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllSuppliers(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  const handleOpenConfirmDialog = (e, row) => {
    openBlockStatusDialog.current.openDialog({
      type: "Supplier",
      status: e ? "Unblock" : "Block",
      data: row,
      event: e,
    });
  };

  const handleUpdateUserStatus = async (params) => {
    try {
      let payload = {
        status: params?.event,
        reason: params.reason,
      };
      setIsLoading(true);
      const response = await updateAllAcountStatus(
        params?.data?._id,
        payload,
        "Supplier"
      );
      if (response.data.status === "success") {
        fetchAllSuppliers(searchKeys.supplierByName);
        handleOpenSnackBar("success", response.data.message);
        openBlockStatusDialog.current.closeDialog({});
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const prepareExportData = () => {
    return allSuppliers?.map((item) => ({
      "Created Date": moment(item.createdAt).format("MM-DD-YYYY"),
      "Business Name": item.businessName,
      Email: item.businessEmail,
      Contact: item.contact,
      Location: item?.location?.addressLabel,
    }));
  };

  const renderHeaderAction = () => {
    return (
      <Stack flexGrow={1} direction={{ md: "row" }} justifyContent={"flex-end"}>
        <CSVLink
          data={prepareExportData()}
          filename={"supplier_list.csv"}
          className="csv-link"
          target="_blank"
        >
          <GlobalButton
            variant={"export_button"}
            btnText={"export"}
            startIcon={<img src={APP_ICONS.exportBlockIcon} width={20} />}
          />
        </CSVLink>
      </Stack>
    );
  };

  const renderSearchAction = () => {
    return (
      <Stack p={2} direction={{ md: "row" }} bgcolor={"primary.light"}>
        <TextInput
          styleProps={{ width: { xs: "100%", md: "35%", xl: "30%" } }}
          InputStartIcon={<img src={APP_ICONS.searchGreyIcon} width={25} />}
          InputEndIcon={
            searchKeys.supplierByName && (
              <CancelOutlinedIcon
                fontSize="small"
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  dispatch(
                    setSearchKeys({
                      ...searchKeys,
                      supplierByName: "",
                    })
                  );
                }}
              />
            )
          }
          placeholder={t("searchByName", { ns: "common" })}
          value={searchKeys.supplierByName}
          onChange={(e) => {
            dispatch(
              setSearchKeys({
                ...searchKeys,
                supplierByName: e.target.value,
              })
            );
          }}
        />
      </Stack>
    );
  };

  const handleNavigateToDetail = (row) => {
    if (!row) return handleOpenSnackBar("error", "No data found");
    navigate(`/supplier-details/${row?._id}`);
  };

  return (
    <>
      <AppLoader isVisible={isLoading} />
      <PaperContainer
        title={t("suppliersLists")}
        subtitle={t("manageSuppliers")}
        // headerActions={allSuppliers?.length > 0 ? renderHeaderAction() : null}
        searchActions={renderSearchAction()}
      >
        <DynamicPaginatedTable
          showPagination={true}
          tableHeader={headerData}
          displayRows={tableRows}
          tableData={allSuppliers}
          handleChangeStatus={handleOpenConfirmDialog}
          showViewDetail={true}
          handleViewDetail={handleNavigateToDetail}
        />
        {allSuppliers?.length === 0 && isLoading === false && (
          <Typography variant="h5" fontWeight={600} textAlign={"center"}>
            {t("noDataFound", { ns: "common" })}
          </Typography>
        )}
      </PaperContainer>
      <StatusBlockDialog
        ref={openBlockStatusDialog}
        onConfirm={handleUpdateUserStatus}
      />
      <ClientOwnerFilterDialog ref={openFilterDialog} />
    </>
  );
}

export default SuppliersList;
